import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { adminApi } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CheckCircle, XCircle, RefreshCw, AlertTriangle, Key } from 'lucide-react'

interface AdminStatusData {
  backendConnected: boolean
  adminApiAccess: boolean
  userRole: string
  permissions: string[]
  error?: string
}

export function AdminStatus() {
  const { user, refreshAdminStatus } = useAuth()
  const [status, setStatus] = useState<AdminStatusData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshingToken, setIsRefreshingToken] = useState(false)

  const checkStatus = async () => {
    setIsLoading(true)
    
    try {
      // Test admin API access
      const adminTest = await adminApi.testAdminAccess()
      
      setStatus({
        backendConnected: true,
        adminApiAccess: adminTest.success,
        userRole: user?.role || 'Unknown',
        permissions: user?.permissions || [],
        error: adminTest.success ? undefined : adminTest.error
      })
    } catch (error: any) {
      setStatus({
        backendConnected: false,
        adminApiAccess: false,
        userRole: user?.role || 'Unknown', 
        permissions: [],
        error: error.message || 'Connection failed'
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      checkStatus()
    }
  }, [user])

  const handleRefresh = async () => {
    try {
      await refreshAdminStatus()
      await checkStatus()
    } catch (error) {
      console.error('Failed to refresh admin status:', error)
    }
  }

  const handleTokenRefresh = async () => {
    setIsRefreshingToken(true)
    try {
      const result = await adminApi.refreshToken()
      if (result.success) {
        console.log('Token refreshed successfully, checking status...')
        await refreshAdminStatus()
        await checkStatus()
      } else {
        console.error('Token refresh failed:', result.error)
      }
    } catch (error) {
      console.error('Token refresh error:', error)
    } finally {
      setIsRefreshingToken(false)
    }
  }

  if (!user) return null

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Admin Status</CardTitle>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTokenRefresh}
              disabled={isRefreshingToken}
              title="Refresh Firebase token (use if you recently got admin permissions)"
            >
              <Key size={16} className={isRefreshingToken ? 'animate-spin' : ''} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Connection Status */}
        <div className="flex items-center space-x-3">
          {status?.backendConnected ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600" />
          )}
          <span className="text-sm">
            Backend Connection: {status?.backendConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>

        {/* API Access */}
        <div className="flex items-center space-x-3">
          {status?.adminApiAccess ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600" />
          )}
          <span className="text-sm">
            Admin API Access: {status?.adminApiAccess ? 'Authorized' : 'Denied'}
          </span>
        </div>

        {/* User Role */}
        <div className="flex items-center space-x-3">
          {user.isAdmin ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
          )}
          <span className="text-sm">
            Role: <span className="font-medium">{status?.userRole}</span>
          </span>
        </div>

        {/* Permissions */}
        {status?.permissions && status.permissions.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Permissions</h4>
            <div className="flex flex-wrap gap-1">
              {status.permissions.map((permission, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                >
                  {permission}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Error Display */}
        {status?.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center space-x-2">
              <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <span className="text-sm text-red-700">{status.error}</span>
                {(status.error.includes('Access denied') || status.error.includes('403') || status.error.includes('Forbidden')) && (
                  <div className="mt-2 text-xs text-red-600">
                    💡 If you recently became an admin, try refreshing your token with the key button above.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Backend URL Info */}
        <div className="pt-2 border-t text-xs text-gray-500">
          Backend: {import.meta.env.VITE_BACKEND_URL || 'http://localhost:3007'}
        </div>
      </CardContent>
    </Card>
  )
}